import type { AddItemRequestBody } from '@libs/common-node';
import { DeliveryType, getCountry } from '@libs/core';
import { useCartConfirmDrawer } from '@libs/shared-ui';

import { findProductsAddedToCart, type UseAddToCartOnSuccess, useAddToCart } from '@/entities/cart';

import type { MappedFBTProduct } from '../lib';
import { useAddToCartAnalytics, useAddToCartEE } from './analytics';

export function useFBTAddToCart(storeId: string) {
    const openCartDrawerWith = useCartConfirmDrawer(state => state.openWith);
    const { sendAnalytics } = useAddToCartAnalytics();
    const { sendAnalyticsEE } = useAddToCartEE();

    function handleAddToCartSuccess({ cart, payload }: UseAddToCartOnSuccess) {
        const productsAddedToCart = findProductsAddedToCart({ cart, payload });

        if (productsAddedToCart.length) {
            openCartDrawerWith(productsAddedToCart);
            sendAnalytics({ products: productsAddedToCart });
            sendAnalyticsEE({ products: productsAddedToCart });
        }
    }

    const { mutateAsync, isPending } = useAddToCart({
        onSuccess: handleAddToCartSuccess,
        isProductRecommendation: true,
    });

    function mapFbtProductsToAddToCartBody(products: MappedFBTProduct[]): AddItemRequestBody[] {
        return products.map(product => ({
            country: getCountry(),
            sku: product.id,
            quantity: 1,
            fulfillmentMethod: DeliveryType.BOPIS,
            storeId,
        }));
    }

    async function addItemsToCart(products: MappedFBTProduct[]) {
        const addToCartItems = mapFbtProductsToAddToCartBody(products);
        await mutateAsync(addToCartItems);
    }

    return { addItemsToCart, isPending };
}
